import { type ReactNode, useState } from "react";
import { FormattedMessage, defineMessages, useIntl } from "react-intl";
import classnames from "classnames";
import { Elements } from "@stripe/react-stripe-js";

import { Payer } from "graphql_globals";
import ProfileSectionHeader from "common/settings/profile_section_header";
import { getLazyStripe } from "common/settings/payment/util";
import { Paragraph } from "common/core/typography";

import PayerSelectRow from "../payer_select_row";
import PaymentQuery, {
  type Payment_node_Organization as Organization,
} from "../graphql/payment_query.graphql";
import { useActiveOrganization } from "../../../account/active_organization";
import { NetworkStatus, useQuery } from "../../../../util/graphql";
import LoadingIndicator from "../../../core/loading_indicator";
import { PaymentInlineForm } from "./inline_form";
import { PaymentModalForm } from "./modal_form";

const messages = defineMessages({
  title: {
    id: "5e7184ce-06cb-4479-81d3-9c355ee30085",
    defaultMessage: "Payment",
  },
});

type PaymentFormProps = {
  onChangesSaved: () => void;
  collectOrgPaymentInfo?: boolean;
  updateDefaultPayer?: boolean;
  useModalStyle?: boolean;
  forceNewPayment?: boolean;
  showDelayedChargeMessage?: boolean;
  showHelp?: boolean;
  showSubscriptionTos?: boolean;
  submitButtonLabel?: ReactNode;
  onClose?: (orgPaymentAdded?: boolean) => void;
};

type PaymentFormInnerType = PaymentFormProps & {
  organization: Organization;
};

function PaymentFormInner(props: PaymentFormInnerType) {
  const {
    organization,
    useModalStyle = false,
    forceNewPayment = false,
    collectOrgPaymentInfo = false,
    showHelp = false,
    showSubscriptionTos = false,
    showDelayedChargeMessage = false,
    submitButtonLabel,
    onChangesSaved,
    onClose,
  } = props;
  const { defaultPayer, defaultPaymentSource } = organization;

  const intl = useIntl();
  const [payer, setPayer] = useState<Payer>(defaultPayer);

  const collectPayment = !useModalStyle || forceNewPayment || !defaultPaymentSource;

  //TODO next PR - update onPaymentChange sig
  const onPaymentChange = (_event: unknown, newValue: Payer) => {
    setPayer(newValue);
  };

  const getPayer = () => {
    if (collectOrgPaymentInfo) {
      return Payer.ORGANIZATION;
    }

    return payer;
  };

  const currentPaymentSource = organization.defaultPaymentSource;
  const { prices } = organization.activePricingPlan;
  const companyName = organization.name;
  const isOrganizationPayer = getPayer() === Payer.ORGANIZATION;
  const isCustomerPayer = getPayer() === Payer.CUSTOMER;

  const paymentDetails = useModalStyle ? (
    <PaymentModalForm
      showSubscriptionTos={showSubscriptionTos}
      showDelayedChargeMessage={showDelayedChargeMessage}
      currentPaymentSource={currentPaymentSource}
      submitButtonLabel={submitButtonLabel}
      onClose={onClose}
      collectPayment={collectPayment}
      stripeElementOptionStyles={{ placeholder: "" }}
      organizationId={organization.id}
      payer={payer}
      onChangesSaved={onChangesSaved}
    />
  ) : (
    <PaymentInlineForm
      currentPaymentSource={currentPaymentSource}
      organizationId={organization.id}
      payer={payer}
      onChangesSaved={onChangesSaved}
      submitButtonLabel={submitButtonLabel}
    />
  );

  return (
    <>
      {!collectOrgPaymentInfo && (
        <div className={classnames(isOrganizationPayer && "section-header-spacing")}>
          {!useModalStyle && (
            <ProfileSectionHeader>{intl.formatMessage(messages.title)}</ProfileSectionHeader>
          )}
          <PayerSelectRow
            fieldName="payer"
            showHelp={showHelp}
            companyName={companyName}
            onChange={onPaymentChange}
            prices={prices}
            useModalStyle={useModalStyle}
          />
        </div>
      )}

      {isOrganizationPayer && paymentDetails}

      {isCustomerPayer && (
        <div className={useModalStyle ? "Form-section-disclaimer-spacing" : "Form-section-header"}>
          <Paragraph size="small" textColor="subtle" className="section-header-disclaimer">
            <FormattedMessage
              id="fa88fd38-17ed-4d4b-8e8d-0457cb7f55a9"
              defaultMessage="Signers will be prompted for payment information when they complete each transaction."
            />
          </Paragraph>
        </div>
      )}
    </>
  );
}

export function PaymentFormV2(props: PaymentFormProps) {
  const [activeOrganizationId] = useActiveOrganization();
  const skip = !activeOrganizationId;
  const { data, networkStatus } = useQuery(PaymentQuery, {
    skip,
    variables: { organizationId: activeOrganizationId! },
  });
  if (skip || !data || networkStatus === NetworkStatus.loading) {
    return <LoadingIndicator />;
  }

  if (data.node?.__typename !== "Organization") {
    throw new Error(`Expected organization, got ${data.node?.__typename}.`);
  }

  return (
    <Elements stripe={getLazyStripe()}>
      <PaymentFormInner {...props} organization={data.node} />
    </Elements>
  );
}
